<template>
    <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors }">
        <modal
            @hidden="clearData"
            @shown="setData"
            :id="modalId"
            :ok-disabled="!meta.valid"
            :title="title"
            @ok.prevent="submit"
        >
            <validation-provider name="name" rules="required">
                <form-input
                    :error="errors.name"
                    v-model="values.name"
                    label="Name"
                    @input="(val) => setFieldValue('name', val)"
                />
            </validation-provider>

            <validation-provider name="description" class="w-100" rules="required" as="div">
                <form-textarea
                    :error="errors.description"
                    v-model="values.description"
                    label="Description"
                    @input="(val) => setFieldValue('description', val)"
                />
            </validation-provider>
            <div class="d-flex justify-content-between input-wrapper align-items-baseline flex-md-row flex-column">
                <validation-provider name="value" class="w-100" rules="validNumber|required" as="div">
                    <form-input
                        class="mb-md-3 mb-0"
                        label="Value"
                        :error="errors.value"
                        v-model="values.value"
                        @input="(val) => setFieldValue('value', val)"
                    />
                </validation-provider>
                <validation-provider name="value_type" class="w-100" rules="required" as="div">
                    <form-select
                        :options="valueOptions"
                        :error="errors.value_type"
                        v-model="values.value_type"
                        @change="(val) => setFieldValue('value_type', val)"
                    />
                </validation-provider>
            </div>
            <validation-provider name="time_type" class="w-100" rules="required" as="div">
                <form-select
                    :options="timeTypeOptions"
                    :error="errors.time_type"
                    v-model="values.time_type"
                    label="Billing"
                    @change="(val) => setFieldValue('time_type', val)"
                />
            </validation-provider>
            <validation-provider name="commissionable" class="w-100" rules="required" as="div">
                <form-select
                    :options="ownerStatementOptions"
                    :error="errors.commissionable"
                    v-model="values.commissionable"
                    label="Owner Statement"
                    @change="(val) => setFieldValue('commissionable', val)"
                />
            </validation-provider>
            <div class="d-flex justify-content-between input-wrapper align-items-baseline flex-md-row flex-column">
                <form-checkbox v-model="values.taxable">Taxable</form-checkbox>
                <form-checkbox v-model="values.discountable">Discountable</form-checkbox>
                <form-checkbox v-model="values.multiple">Multiple</form-checkbox>
                <form-checkbox v-model="values.internal">Internal</form-checkbox>
            </div>
            <form-search-multiselect
                v-if="values.taxable"
                v-model="values.tax_exceptions"
                label="Taxes"
                :options="taxOptions"
                placeholder="Select Taxes"
            />
        </modal>
    </validation-observer>
</template>

<script>
    import { mapGetters } from 'vuex';
    import formInput from '../../generic/form-input/form-input.vue';
    import formSelect from '../../generic/form-select/form-select.vue';
    import modal from '../../generic/modal/modal.vue';
    import formTextarea from '../../generic/form-textarea/form-textarea.vue';
    import formCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
    import formSearchMultiselect from '../../generic/form-search-multiselect/form-search-multiselect.vue';

    export default {
        name: 'AddEditTaxModal',
        components: {
            formInput,
            formSelect,
            modal,
            formTextarea,
            formCheckbox,
            formSearchMultiselect,
        },
        props: {
            initialData: {
                type: Object,
                default: null,
            },
            modalId: {
                type: String,
            },
        },
        data() {
            return {
                values: {
                    name: 'New Service',
                    value: '',
                    value_type: 0,
                    internal: false,
                    multiple: false,
                    taxable: false,
                    discountable: false,
                    description: 'New Service',
                    tax_exceptions: [],
                    time_type: 0,
                    commissionable: 0,
                },
            };
        },
        computed: {
            ...mapGetters('propertyManager', ['metadata']),
            title() {
                return this.initialData ? 'Edit Service' : 'Add Service';
            },
            timeTypeOptions() {
                return this.metadata.time_type_choices.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
            },
            taxOptions() {
                return this.metadata?.taxes.map((item) => ({
                    value: item.id,
                    text: item.title,
                }));
            },
            ownerStatementOptions() {
                return [
                    {
                        value: 0,
                        text: 'Not Applicable',
                    },
                    {
                        value: 1,
                        text: 'Include as revenue (commissioned)',
                    },
                    {
                        value: 2,
                        text: 'Include as revenue (no commission)',
                    },
                ];
            },
            valueOptions() {
                return [
                    {
                        value: 0,
                        text: 'Fixed',
                    },
                    {
                        value: 1,
                        text: 'Percent % Rental Charges',
                    },
                    {
                        value: 2,
                        text: 'Percent % Total Charges',
                    },
                ];
            },
        },
        methods: {
            submit() {
                this.$emit('submit', {
                    ...this.values,
                    tax_exceptions: this.taxOptions
                        .filter((item) => !this.values.tax_exceptions.includes(item.value))
                        .map((item) => item.value),
                    time_type: this.metadata.time_type_choices.find((item) => item.id == this.values.time_type),
                    person_type: this.values.multiple,
                });
            },
            setData() {
                if (this.initialData) {
                    this.values = {
                        ...this.initialData,
                        time_type: this.initialData.time_type.id,
                        tax_exceptions: this.metadata?.taxes
                            .map((item) => item.id)
                            .filter((item) => !this.initialData.tax_exceptions.map((item) => item.tax).includes(item)),
                    };
                } else {
                    this.values = {
                        name: 'New Service',
                        value: '',
                        value_type: 0,
                        internal: false,
                        multiple: false,
                        taxable: false,
                        discountable: false,
                        description: 'New Service',
                        tax_exceptions: [],
                        time_type: this.timeTypeOptions[0].value,
                        commissionable: 0,
                    };
                }
                this.$refs.form.setValues({
                    ...this.values,
                });
            },
            clearData() {
                this.$emit('hide');
                this.values = {
                    name: 'New Service',
                    value: '',
                    value_type: 0,
                    internal: false,
                    multiple: false,
                    taxable: false,
                    discountable: false,
                    description: '',
                    tax_exceptions: [],
                    time_type: 0,
                    commissionable: 0,
                };
            },
        },
        watch: {
            initialData() {
                this.setData();
            },
        },
    };
</script>

<style scoped>
    .input-wrapper {
        gap: 1rem;
    }
</style>
